/**
 * JavaScript error severity classification strategy
 */

import type { SeverityStrategy, ErrorContext } from './SeverityStrategy.js';
import type { Severity } from '../../types.js';

export class JavaScriptErrorSeverity implements SeverityStrategy {
  canHandle(context: ErrorContext): boolean {
    return context.type === 'javascript';
  }

  classify(context: ErrorContext): Severity {
    const errorString = typeof context.error === 'string' ? context.error : context.error.message;
    const errorStack = context.stack || '';

    // CRITICAL: App-breaking errors that need immediate attention
    if (
      errorString.includes('ChunkLoadError') ||
      errorString.includes('Loading chunk') ||
      errorString.includes('Module not found') ||
      errorString.includes('Script error') ||
      (errorString.includes('Cannot read propert') && errorString.includes('undefined')) ||
      errorString.includes('null is not an object') ||
      errorString.includes('is not a function') ||
      errorStack.includes('ReferenceError') ||
      (errorStack.includes('is not defined') && !errorStack.includes('console'))
    ) {
      return 'critical';
    }

    // HIGH: Serious errors that impact functionality
    if (
      errorString.includes('TypeError') ||
      errorString.includes('ReferenceError') ||
      errorString.includes('SyntaxError') ||
      errorString.includes('RangeError') ||
      errorString.includes('SecurityError')
    ) {
      return 'high';
    }

    // MEDIUM: Errors that may impact some users
    if (
      errorString.includes('NetworkError') ||
      errorString.includes('fetch failed') ||
      errorString.includes('Failed to fetch') ||
      errorString.includes('timeout') ||
      errorString.includes('timed out') ||
      errorString.includes('AbortError') ||
      errorString.includes('Load failed')
    ) {
      return 'medium';
    }

    // LOW: Minor issues, warnings, or non-critical errors
    return 'low';
  }
}

